import { omit } from 'lodash';
import buildSearchLink from './buildSearchLink';
import {
	parseAmenityFromPath,
	getFilterValueForAmenity,
} from './parseAmenityFromPath';

/**
 * Search-context keys that describe WHERE the search happens, not WHAT is
 * being filtered. When redirecting an amenity LSA page back to its city CLP,
 * the city/state live in the destination pathname, so they must be stripped
 * from the query string to avoid a redundant ?city=...&state=... CLP URL.
 *
 * `amenities` is handled separately (surgically) so co-selected amenities are
 * preserved — only the amenity tied to the LSA path is removed.
 */
const CLP_CONTEXT_KEYS = [
	'city',
	'state',
	'location',
	'subPageType',
	'pageType',
	'amenity',
	'type',
	'vehicle',
	'page',
];

/**
 * Decides where an "Apply Filters" / filter-change navigation should go.
 *
 * Most searches only mutate the query string (`{ search }`). But amenity LSA
 * pages (e.g. /self-storage/texas/austin/climate-controlled/) encode the
 * amenity in the URL *path*, not the query string. Clearing that amenity and
 * re-applying must change the *pathname* back to the city CLP — otherwise the
 * route re-seeds the amenity from the still-present path slug and the filter
 * appears stuck (SF-21011).
 *
 * @param {Object} args
 * @param {string} args.pathname  Current location pathname (pre-navigation).
 * @param {Object} args.params    Final, pickBy-cleaned search params to apply.
 * @param {string} [args.cityUrl] City CLP path from the store (e.g.
 *                                "/self-storage/texas/austin/").
 * @returns {{ search: string, pathname?: string }} A react-router navigate target.
 */
export default function resolveSearchNavigation({ pathname, params, cityUrl }) {
	const amenitySlug = parseAmenityFromPath(pathname);
	const amenityFilterKey = amenitySlug
		? getFilterValueForAmenity(amenitySlug)
		: null;

	// The boolean param is the reliable "is this amenity checked" signal shared
	// by both filter UIs (the new tray clears the boolean but leaves a stale
	// `amenities` array; the old modal re-derives clean params via the selector).
	// Redirect only when we're on an amenity LSA path, that amenity's boolean is
	// gone, and we have a CLP destination to navigate to.
	const amenityCleared =
		amenityFilterKey && !params[amenityFilterKey] && !!cityUrl;

	if (!amenityCleared) {
		return { search: buildSearchLink(params) };
	}

	// Drop context keys (city/state/amenity slug) since they're encoded in the
	// destination pathname; keep the remaining user-selected filters.
	const remainingFilters = omit(params, CLP_CONTEXT_KEYS);

	// Surgically remove the LSA amenity from the array (old FilterGroup UI),
	// preserving any co-selected amenities. Drop the array entirely if empty.
	if (Array.isArray(remainingFilters.amenities)) {
		const kept = remainingFilters.amenities.filter(
			(value) => value !== amenityFilterKey,
		);
		remainingFilters.amenities = kept.length ? kept : undefined;
	}

	const search = buildSearchLink(remainingFilters);

	return {
		pathname: cityUrl,
		// buildSearchLink returns "?" for an empty param set; normalize to ""
		// so the city CLP URL stays clean when no other filters remain.
		search: search === '?' ? '' : search,
	};
}
